import React, { useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ShowFeedModal } from '../../../../components/DataFeedWizard/ShowFeedModal/ShowFeedModal';
import StyledDataGrid from '../../../../components/DataGrid/DataGridStyles';
import { SearchBar } from '../../../SearchBar/SearchBar';
import { CustomAutocomplete } from '../../../Infra/CustomAutocomplete/CustomAutocomplete';
import { ModifiedIndicator } from '../../../Infra/ModifiedIndicator';
import { StatusIndicator } from '../../../Infra/StatusIndicator';
import { useLinkOTCStep } from './useLinkOTCStep';
import { LinkOTCStepProps } from './types';
import {
  OTCMatchItem,
  InstrumentSourceId,
} from '../../../../types/generatedTypes';
import { getNumericStringSortComparator } from '../../../../utils/sortComparators';
import { StepMetadata } from '../../../../pages/DataFeedWizard/types';
import styles from './LinkOTCStep.module.scss';

export const LinkOTCStep: React.FC<LinkOTCStepProps> = props => {
  const {
    rows,
    otcColumns,
    illioOTCOptions,
    handleIllioOTCChange,
    handleMoveToInstruments,
    isSyncing,
    handleRowUpdate,
    searchTerm,
    handleTableSearch,
    isItemDisabledAccountOnly,
    prices,
  } = useLinkOTCStep();

  const [feedModalOpen, setFeedModalOpen] = useState(false);
  const [feedFilter, setFeedFilter] = useState<{
    symbol?: string | null;
    isin?: string | null;
  }>({});

  const columns: GridColDef[] = useMemo(() => {
    // Generate dynamic OTC columns based on configureMappingData.otcColumns
    const otcColumnDefs = otcColumns.map(colName => ({
      field: colName,
      headerName: colName,
      width: 200,
      renderCell: (params: GridRenderCellParams<OTCMatchItem>) =>
        (params.row as Record<string, unknown>)[colName] || '',
    }));

    return [
      {
        field: 'status',
        headerName: '',
        width: 40,
        minWidth: 40,
        align: 'center',
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) => (
          <StatusIndicator status={params.row.status} />
        ),
      },
      {
        field: 'feed_info',
        headerName: '',
        width: 60,
        minWidth: 60,
        sortable: false,
        align: 'center',
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) => {
          if (!params.row.symbol && !params.row.isin) return null;
          return (
            <Tooltip title="View feed transactions">
              <IconButton
                size="small"
                onClick={() => {
                  setFeedFilter({
                    symbol: params.row.symbol,
                    isin: params.row.isin,
                  });
                  setFeedModalOpen(true);
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'instrument_type',
        headerName: 'Type',
        width: 180,
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) => {
          const isDisabled = isItemDisabledAccountOnly(params.row.account_numbers);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
              {params.value}
              {isDisabled && (
                <Tooltip title="This OTC instrument appears only in disabled accounts">
                  <Chip label="Disabled" size="small"
                    sx={{ backgroundColor: '#fff8e1', color: '#f9a825', border: '1px solid #ffe082', fontSize: '0.7rem', height: 20 }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: 'instrument_source_id',
        headerName: 'Illio OTC',
        flex: 1,
        minWidth: 300,
        // Sort by row.name (displayed instrument name) since instrument_source_id is an object
        valueGetter: (
          _value: InstrumentSourceId | null | undefined,
          row: OTCMatchItem
        ) => row.name || '',
        getSortComparator: getNumericStringSortComparator,
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) => {
          const selectedOption =
            illioOTCOptions.find(
              opt =>
                opt.instrument_source_id?.source_id ===
                params.row.instrument_source_id?.source_id
            ) || null;

          return (
            <CustomAutocomplete
              value={selectedOption}
              onChange={(_, value) =>
                handleIllioOTCChange(params.row.id, value)
              }
              options={illioOTCOptions}
              getOptionLabel={option =>
                `${option.name} | ${option.code || '—'} | ${option.instrument_type || '—'}`
              }
              renderOption={(props, option) => (
                <li {...props} key={option.otc_db_id}>
                  {option.name} | {option.code || '—'} |{' '}
                  {option.instrument_type || '—'}
                </li>
              )}
              placeholder="Select OTC..."
              className={styles.illioOTCAutocomplete}
            />
          );
        },
      },
      {
        field: 'isin',
        headerName: 'ISIN',
        width: 180,
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) =>
          params.row.isin || '',
      },
      {
        field: 'symbol',
        headerName: 'Symbol',
        width: 150,
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) =>
          params.row.symbol || '',
      },
      {
        field: 'price',
        headerName: 'Price',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) => {
          if (!params.row.instrument_source_id?.source_id) return '';
          const priceInfo = prices[params.row.id];
          if (!priceInfo) return '';
          if (priceInfo.loading) return <CircularProgress size={16} />;
          if (priceInfo.error || priceInfo.value === null)
            return (
              <span style={{ color: '#d32f2f', fontSize: '0.8rem' }}>
                PRICE UNAVAILABLE
              </span>
            );
          return priceInfo.value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
          });
        },
      },
      ...otcColumnDefs, // Dynamic OTC columns
      {
        field: 'multiplier',
        headerName: 'Multiplier',
        width: 120,
        editable: true,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        sortable: true,
        valueSetter: (value, row) => {
          if (value === 0) {
            return row;
          }
          return { ...row, multiplier: value };
        },
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) => {
          if (!params.row.instrument_source_id?.source_id) {
            return '';
          }
          const isModified =
            params.row.original_multiplier != null &&
            params.row.multiplier !== params.row.original_multiplier;
          return (
            <ModifiedIndicator isModified={isModified} value={params.value} />
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        sortable: false,
        renderCell: (params: GridRenderCellParams<OTCMatchItem>) => {
          return (
            <Tooltip title="Reclassify this security as standard instrument">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleMoveToInstruments(params.row.id)}
                sx={{ '&:hover': { backgroundColor: 'transparent' } }}
              >
                <ArrowBackIcon fontSize="small" />
                <span style={{ marginLeft: 4, fontSize: '0.75rem' }}>
                  Move to Instruments
                </span>
              </IconButton>
            </Tooltip>
          );
        },
      },
    ];
  }, [
    otcColumns,
    illioOTCOptions,
    handleIllioOTCChange,
    handleMoveToInstruments,
    isItemDisabledAccountOnly,
    prices,
  ]);

  return (
    <Box className={styles.container}>
      <Box className={styles.section}>
        <Box className={styles.controlsRow}>
          <SearchBar
            onSearch={handleTableSearch}
            value={searchTerm}
            placeholder="Search OTC instruments..."
          />
        </Box>
        <Box className={styles.gridContainer}>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            getRowId={row => row.id}
            loading={isSyncing}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooter
            density="standard"
            showCellVerticalBorder
            processRowUpdate={handleRowUpdate}
            onProcessRowUpdateError={error =>
              console.error('Row update error:', error)
            }
          />
        </Box>
      </Box>
      {feedModalOpen && (
        <ShowFeedModal
          open={feedModalOpen}
          onClose={() => setFeedModalOpen(false)}
          filterBy={feedFilter}
        />
      )}
    </Box>
  );
};

export const stepMetadata: StepMetadata = {
  title: 'Link OTC Instruments',
  subtitle:
    'Match OTC securities to existing OTC instruments or reclassify as standard instruments',
  icon: <AccountBalanceIcon />,
};
